import { useSheet } from "@/elements/components/shared/sheet-provider";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Select } from "@/elements/components/ui/select";
import { SheetIds } from "@/elements/components/utils/sheet-ids";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { useCallback, useMemo, useState } from "react";
import { ScrollView, Text, View } from "react-native";
import { useAuthActions } from "@convex-dev/auth/react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/elements/components/utils/convex-api";
import { convexQuery } from "@convex-dev/react-query";
import { Id } from "@teachloop/backend/_generated/dataModel";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { TeachingExperienceFormData } from "./teaching-experience";
import { isIbCurriculumLabel } from "@/elements/components/utils/curriculum";

const safeParse = (value: string) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    console.error(error);
    return null;
  }
};

function CurriculumAndSubjectsScreen() {
  const router = useRouter();
  const { signOut } = useAuthActions();
  const { teachingExperience } = useLocalSearchParams();
  const teachingExperienceData = safeParse(
    teachingExperience as string,
  ) as TeachingExperienceFormData;
  const { open } = useSheet<any, any>(SheetIds.MULTI_SELECT);
  const [selectedSubjectOptions, setSelectedSubjectOptions] = useState<
    string[]
  >([]);
  const [selectedCurriculumOptions, setSelectedCurriculumOptions] = useState<
    string[]
  >([]);
  const [selectedProgrammeOptions, setSelectedProgrammeOptions] = useState<
    string[]
  >([]);

  const { data: subjects = [] } = useQuery(
    convexQuery(api.tutor.onboarding.queries.getAllSubjects, {}),
  );
  const { data: curriculums = [] } = useQuery({
    ...convexQuery(api.tutor.onboarding.queries.getAvailableCurriculums, {}),
  });

  const hasIbCurriculum = useMemo(
    () =>
      selectedCurriculumOptions.some((curriculumId) =>
        isIbCurriculumLabel(
          curriculums.find((curriculum) => curriculum.value === curriculumId)
            ?.label,
        ),
      ),
    [selectedCurriculumOptions, curriculums],
  );

  const ibCurriculumId = useMemo(
    () =>
      curriculums.find((curriculum) =>
        isIbCurriculumLabel(curriculum.label),
      )?.value,
    [curriculums],
  );

  const { data: programmes = [] } = useQuery(
    convexQuery(
      api.tutor.onboarding.queries.getProgrammesForCurriculum,
      hasIbCurriculum && ibCurriculumId
        ? { curriculumId: ibCurriculumId as Id<"curriculum"> }
        : "skip",
    ),
  );

  const isFormValid = useMemo(() => {
    const hasSubjectsAndCurriculums =
      selectedSubjectOptions.length > 0 &&
      selectedCurriculumOptions.length > 0;
    if (!hasSubjectsAndCurriculums) return false;
    if (hasIbCurriculum && selectedProgrammeOptions.length === 0) return false;
    return true;
  }, [
    selectedSubjectOptions,
    selectedCurriculumOptions,
    hasIbCurriculum,
    selectedProgrammeOptions,
  ]);

  const handleSubjectOptionsOpen = useCallback(async () => {
    const result = await open({
      options: subjects,
      preSelectedOptions: selectedSubjectOptions,
      title: "Which subjects can you teach?",
    });
    if (result?.selectedOptions) {
      setSelectedSubjectOptions(result.selectedOptions);
    }
  }, [open, selectedSubjectOptions, subjects]);

  const handleCurriculumOptionsOpen = useCallback(async () => {
    const result = await open({
      options: curriculums,
      preSelectedOptions: selectedCurriculumOptions,
      title: "Which curriculum do you teach?",
    });
    if (!result?.selectedOptions) return;

    const nextCurriculumIds = result.selectedOptions;
    const nextHasIb = nextCurriculumIds.some((curriculumId: string) =>
      isIbCurriculumLabel(
        curriculums.find((curriculum) => curriculum.value === curriculumId)
          ?.label,
      ),
    );

    setSelectedCurriculumOptions(nextCurriculumIds);
    if (!nextHasIb) {
      setSelectedProgrammeOptions([]);
    }
  }, [open, selectedCurriculumOptions, curriculums]);

  const handleProgrammeOptionsOpen = useCallback(async () => {
    const result = await open({
      options: programmes,
      preSelectedOptions: selectedProgrammeOptions,
      title: "Which IB programmes can you teach?",
    });
    if (result?.selectedOptions) {
      setSelectedProgrammeOptions(result.selectedOptions);
    }
  }, [open, selectedProgrammeOptions, programmes]);

  const handleNext = useCallback(async () => {
    router.push(
      `/(app)/onboarding/(tutor)/year-groups?teachingExperienceWithCurriculumsAndSubjects=${encodeURIComponent(
        JSON.stringify({
          curriculumIds: selectedCurriculumOptions as Id<"curriculum">[],
          programmeIds: selectedProgrammeOptions as Id<"keyStages">[],
          subjectYearGroups: (selectedSubjectOptions as Id<"subjects">[]).map(
            (subjectId) => ({
              subjectId,
              yearGroupIds: [],
            }),
          ),
          ...teachingExperienceData,
        }),
      )}`,
    );
  }, [
    selectedCurriculumOptions,
    selectedProgrammeOptions,
    selectedSubjectOptions,
    router,
    teachingExperienceData,
  ]);

  if (!teachingExperienceData) {
    return <Redirect href="/(app)/onboarding/(tutor)/teaching-experience" />;
  }

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon onPress={() => signOut()} />
        <AppBar.Title>Sign out</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                What is your subject and curriculum experience?
              </Text>
              <Text className="font-geologica-regular font-normal text-xl text-black leading-[1.5]">
                This helps us personalise your experience.
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-6">
              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Which curriculum do you teach?
                </Text>
                <Select size="md" onPress={handleCurriculumOptionsOpen}>
                  <Select.Label size="md">
                    {selectedCurriculumOptions.length > 0
                      ? "Selected curriculum"
                      : "Which curriculum do you teach?"}
                  </Select.Label>
                  <Select.DownArrow />
                </Select>
                <Select.Results
                  selectedOptions={curriculums.filter((option) =>
                    selectedCurriculumOptions.includes(option.value),
                  )}
                  onRemove={(value) => {
                    const nextCurriculumIds = selectedCurriculumOptions.filter(
                      (option) => option !== value,
                    );
                    setSelectedCurriculumOptions(nextCurriculumIds);
                    const nextHasIb = nextCurriculumIds.some((curriculumId) =>
                      isIbCurriculumLabel(
                        curriculums.find(
                          (curriculum) => curriculum.value === curriculumId,
                        )?.label,
                      ),
                    );
                    if (!nextHasIb) {
                      setSelectedProgrammeOptions([]);
                    }
                  }}
                />
              </View>
              {hasIbCurriculum ? (
                <View className="gap-4">
                  <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                    Which IB programmes can you teach?
                  </Text>
                  <Select size="md" onPress={handleProgrammeOptionsOpen}>
                    <Select.Label size="md">
                      {selectedProgrammeOptions.length > 0
                        ? "Selected programmes"
                        : "Select IB programmes"}
                    </Select.Label>
                    <Select.DownArrow />
                  </Select>
                  <Select.Results
                    selectedOptions={programmes.filter((option) =>
                      selectedProgrammeOptions.includes(option.value),
                    )}
                    onRemove={(value) => {
                      setSelectedProgrammeOptions(
                        selectedProgrammeOptions.filter(
                          (option) => option !== value,
                        ),
                      );
                    }}
                  />
                </View>
              ) : null}
              <View className="gap-4">
                <Text className="font-geologica-medium leading-6 font-medium text-[16px] text-teachLoop-textPrimary">
                  Which subjects can you teach?
                </Text>
                <Select size="md" onPress={handleSubjectOptionsOpen}>
                  <Select.Label size="md">
                    {selectedSubjectOptions.length > 0
                      ? "Selected subjects"
                      : "Select one or more subjects"}
                  </Select.Label>
                  <Select.DownArrow />
                </Select>
                <Select.Results
                  selectedOptions={subjects.filter((option) =>
                    selectedSubjectOptions.includes(option.value),
                  )}
                  onRemove={(value) => {
                    setSelectedSubjectOptions(
                      selectedSubjectOptions.filter(
                        (option) => option !== value,
                      ),
                    );
                  }}
                />
              </View>
            </View>
            <View className="h-8" />
            <Button
              size="md"
              className="w-full"
              intent="primary"
              onPress={handleNext}
              disabled={!isFormValid}
            >
              <Button.Text size="md">Next</Button.Text>
            </Button>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default CurriculumAndSubjectsScreen;
